import { FC, useEffect, useState } from 'react';
import { Grid as KendoGrid, GridCellProps, GridColumn, GridDetailExpandChangeEvent } from '@progress/kendo-react-grid';
import useCustomer from 'src/hooks/useCustomer';
import { State as ODataState } from '@progress/kendo-data-query';
import ItemApi from 'src/api/ItemApi';
import { ItemAvailability } from 'src/types/itemAvailability'; // Assuming ItemAvailability does not need 'expanded' property
import ItemDetailActivity from './ItemDetailActivity';
import CircularProgress from '@mui/material/CircularProgress';
import { DetailExpandDescriptor } from '@progress/kendo-react-data-tools';
import LeadTimeCell from 'src/components/grid/LeadTimeCell';
import { toString } from '@progress/kendo-intl';

declare interface IProps {
  itemNumber: string;
  visible: boolean;
  sfaOrder: number;
  orderNumber: number;
  orderSuffix: number;
}

const NextReceiptDateCell: FC<GridCellProps> = ({ dataItem, field }) => {
  const rawValue = field ? dataItem[field] : null;
  const dateValue = rawValue ? new Date(rawValue) : null;
  const isValidDate = dateValue != null && !isNaN(dateValue.getTime());
  // Date of 1/1/2011 is used as a flag for vendor credit hold
  const isTbdDate =
    isValidDate &&
    dateValue.getFullYear() === 2011 &&
    dateValue.getMonth() === 0 &&
    dateValue.getDate() === 1;

  let displayValue = '';
  if (isTbdDate) {
    displayValue = 'TBD';
  } else if (isValidDate) {
    displayValue = toString(dateValue, 'MM/dd/yyyy');
  }

  return <td>{displayValue}</td>;
};

const ItemDetailAvailability: FC<React.PropsWithChildren<IProps>> = ({ itemNumber, visible }) => {
  const { customer } = useCustomer();
  const [availability, setAvailability] = useState<ItemAvailability[] | undefined>(undefined);
  const [gridDataState, setGridDataState] = useState<ODataState>({});
  const [isLoading, setIsLoading] = useState(false);
  const [expandedDetailState, setExpandedDetailState] = useState<DetailExpandDescriptor>({});

  function reposition(arr, old_index, new_index) {
    if (new_index >= arr.length) {
      var k = new_index - arr.length + 1;
      while (k--) {
        arr.push(undefined);
      }
    }
    arr.splice(new_index, 0, arr.splice(old_index, 1)[0]);
    return arr;
  }

  useEffect(() => {
    if (itemNumber && customer) {
      // Ensure customer is also available
      setIsLoading(true);
      ItemApi.getItemAvailability(itemNumber)
        .then((data) => {
          if (data) {
            data.forEach((d) => (d.nextReceiptDate = d.nextReceiptDate == null ? null : new Date(d.nextReceiptDate)));

            var backupWhseIndex = data.findIndex((d) => d.warehouseCode == customer.backupWarehouseCode);
            if (backupWhseIndex >= 0) {
              data[backupWhseIndex].warehouseDisplay = `**${data[backupWhseIndex].warehouseDisplay}`;
              reposition(data, backupWhseIndex, 0);
            }

            var defaultWhseIndex = data.findIndex((d) => d.warehouseCode == customer.warehouseCode);
            if (defaultWhseIndex >= 0) {
              data[defaultWhseIndex].warehouseDisplay = `*${data[defaultWhseIndex].warehouseDisplay}`;
              reposition(data, defaultWhseIndex, 0);
            }
            setAvailability(data);
          } else {
            setAvailability([]);
          }
          setIsLoading(false);
        })
        .catch(() => {
          setAvailability([]);
          setIsLoading(false);
        });
    } else {
      setAvailability([]); // Clear data if itemNumber or customer is not available
      setExpandedDetailState({}); // Reset expanded state
    }
  }, [gridDataState, itemNumber, customer]); // Added customer to dependency array

  const handleGridDataStateChange = (e: any) => {
    setGridDataState(e.dataState);
  };

  const handleDetailExpandChange = (event: GridDetailExpandChangeEvent) => {
    setExpandedDetailState(event.detailExpand);
  };

  return (
    <div role="tabpanel" hidden={!visible} id="item-availability" aria-labelledby="item-availability-tab">
      {isLoading ? (
        <CircularProgress sx={{ ml: 2, mt: 2 }} />
      ) : (
        <KendoGrid
          data={availability}
          scrollable="scrollable"
          dataItemKey="warehouseCode"
          detail={ItemDetailActivity}
          detailExpand={expandedDetailState}
          onDetailExpandChange={handleDetailExpandChange}
          onDataStateChange={handleGridDataStateChange}
          {...gridDataState}
        >
          <GridColumn field="warehouseDisplay" title="Whse" width={200} />
          <GridColumn field="onHand" title="On Hand" width={90} />
          <GridColumn field="onSalesOrder" title="Reserved/Committed" width={130} />
          <GridColumn field="netAvailableCurrent" title="Avail (Current)" className="bold" width={130} />
          <GridColumn field="xferOut" title="WT Out" width={80} />
          <GridColumn field="xferIn" title="WT In" width={80} />
          <GridColumn field="onPO" title="On P/O" width={80} />
          <GridColumn field="netAvailable" title="Avail (Future)" className="bold" width={115} />
          <GridColumn cells={{ data: NextReceiptDateCell }} field="nextReceiptDate" title="Next Receipt Date" width={150} />
          <GridColumn field="stockStatus" title="Stock Status" width={140} />
          <GridColumn cells={{ data: LeadTimeCell }} field="avgLeadTimeDays" title="Avg Lead Time (d)" width={145} />
          <GridColumn field="" title="" editable={false} />
        </KendoGrid>
      )}
    </div>
  );
};

export default ItemDetailAvailability;
